import { useMemo } from "react";
import { Link } from "react-router-dom";
import { IconShieldCheck } from "@tabler/icons-react";
import logo from "../../../../assets/images/logo.svg";
import type { SaleDetailsDto } from "../../../../api";
import { useCartStore, type CartItem } from "../../../../hooks/stores/useCartStore";
import { Image } from "../../../../components/common/image";
import { Heading, Text } from "../../../../components/common/typography";
import { CurrencyAmount } from "../../../../components/common/currency_amount";
import { GatedPrice } from "../../../../components/common/gated-price";
import { Button } from "../../../../components/common/button";
import { Switch } from "../../../../components/common/switch/switch";
import { useMe } from "../../../../swr/useMe";
import { useBuyerLoyaltyConfig } from "../../../loyalty/hooks/useBuyerLoyaltyConfig";
import { useActiveLoyaltyPromotions } from "../../../loyalty/hooks/useLoyaltyPromotions";
import { calculateCartEarn } from "../../../loyalty/utils/calculate-cart-earn";
import { LoyaltyEarnSummary } from "../../../loyalty/presentation/components/loyalty-earn-summary";
import { useCheckoutStore } from "../../domain/useCheckoutStore";
import { getProductLineTotal, getProductUnitPrice } from "../../../products/utils/get-pricelist-price";
import { getProductDetailsUrl } from "../../../products/utils/get-product-details-url";

function taxLabel(tax: number, subtotal: number): string {
  if (tax > 0 && subtotal > 0) {
    const pct = Math.round((tax / subtotal) * 100);
    return `Tax (${pct}%)`;
  }
  return "Tax";
}

interface BasketOrderSummaryProps {
  currency?: string;
  onProceed: () => void;
  buttonLabel?: string;
  /** Disables the proceed button while the step isn't ready to advance. */
  proceedDisabled?: boolean;
  sale?: SaleDetailsDto;
  items?: CartItem[] | null;
}

export function BasketOrderSummary({
  currency = "QAR",
  onProceed,
  buttonLabel = "Proceed to Checkout",
  proceedDisabled = false,
  sale,
  items: itemsProp,
}: BasketOrderSummaryProps) {
  const { items } = useCartStore();
  const { user } = useMe();
  const { data: loyaltyConfig } = useBuyerLoyaltyConfig();
  const { data: activePromotions } = useActiveLoyaltyPromotions();
  const { loyaltyPointsToRedeem, setLoyaltyPointsToRedeem } = useCheckoutStore();
  const isSaleCheckout = sale != null;
  const checkoutItems = itemsProp ?? items;
  const effectiveCurrency = sale?.currency?.name ?? currency;
  const totalItems = isSaleCheckout
    ? sale.orderLines.reduce((sum, line) => sum + line.quantity, 0)
    : checkoutItems.reduce((sum, item) => sum + item.quantity, 0);
  const displayLines = useMemo(
    () =>
      isSaleCheckout
        ? sale.orderLines.map((line) => ({
            key: String(line.id),
            image: line.image,
            alt: line.name || line.product?.name || "Product",
            title: line.name || line.product?.name || "Product",
            quantity: line.quantity,
            amount: line.total,
            // SaleOrderLineDto only carries an Odoo {id,name} for the product
            // (no productTemplateId), so we can't build a reliable details URL
            // for sale-checkout rows. Cart rows below get a real link.
            detailsUrl: null as string | null,
          }))
        : checkoutItems.map((item) => {
            const unitPrice = getProductUnitPrice(item.product, item.quantity);
            const baseUnitPrice = getProductUnitPrice(item.product, 1);
            const discount = baseUnitPrice > 0 && unitPrice < baseUnitPrice
              ? Math.round(((baseUnitPrice - unitPrice) / baseUnitPrice) * 100)
              : 0;
            return {
              key: String(item.product.id),
              image: item.product.image ?? null,
              alt: item.product.displayName ?? item.product.name ?? "Product",
              title: item.product.displayName ?? item.product.name ?? "Product",
              quantity: item.quantity,
              amount: getProductLineTotal(item.product, item.quantity),
              unitPrice,
              discount,
              detailsUrl: getProductDetailsUrl(
                item.product.productTemplateId ?? item.product.id,
                item.product.id,
              ) as string | null,
            };
          }),
    [checkoutItems, isSaleCheckout, sale],
  );

  const warrantyLines = isSaleCheckout
    ? []
    : checkoutItems
        .filter((i) => i.warranty)
        .map((i) => ({
          key: `${i.product.id}-${i.warranty!.productId}`,
          productName: i.product.displayName ?? i.product.name ?? "Product",
          warrantyName: i.warranty!.name,
          quantity: i.quantity,
          lineTotal: i.warranty!.price * i.quantity,
        }));
  const warrantiesSubtotal = warrantyLines.reduce((sum, w) => sum + w.lineTotal, 0);

  const subtotal = isSaleCheckout
    ? sale.amountUntaxed
    : checkoutItems.reduce(
        (sum, item) => sum + getProductLineTotal(item.product, item.quantity),
        0,
      ) + warrantiesSubtotal;
  const productsSubtotal = isSaleCheckout ? subtotal : subtotal - warrantiesSubtotal;
  const vat = isSaleCheckout ? sale.amountTax : null;
  const grossTotal = isSaleCheckout ? sale.amountTotal : subtotal;

  const balance = user?.loyaltyPoints ?? 0;
  // `redemptionValuePer100Points` is added to the generated DTO after
  // `npm run generate:api`. Cast disappears post-regen.
  const redemptionRate =
    (loyaltyConfig as { redemptionValuePer100Points?: number } | undefined)
      ?.redemptionValuePer100Points ?? 1;

  // Cap the redeemable amount at the gross total so the discount can never
  // drive the order negative. Round down to the nearest 100 because the
  // redemption rate is expressed per 100 points.
  const maxRedeemableByOrder = useMemo(() => {
    if (redemptionRate <= 0) return 0;
    const pointsForFullCover = Math.floor((grossTotal / redemptionRate) * 100);
    return Math.max(0, Math.floor(pointsForFullCover / 100) * 100);
  }, [grossTotal, redemptionRate]);
  const maxRedeemable = Math.min(balance, maxRedeemableByOrder);
  const canRedeem = maxRedeemable > 0;

  const appliedPoints = loyaltyPointsToRedeem ?? 0;
  const safeAppliedPoints =
    appliedPoints > maxRedeemable ? maxRedeemable : appliedPoints;
  const loyaltySavings = (safeAppliedPoints / 100) * redemptionRate;
  const total = Math.max(0, grossTotal - loyaltySavings);

  // Buyers earn points on the amount they actually pay (net total, after a
  // points-redemption discount). Promotions are resolved per product so the
  // checkout can show a per-product breakdown instead of a blended factor.
  // Sale-checkout lines carry no product loyalty data, so they earn nothing here.
  const pointsPer100 = loyaltyConfig?.pointsPer100 ?? 0;
  const earn = useMemo(
    () =>
      isSaleCheckout
        ? { lines: [], total: 0, hasBonus: false, isMixed: false }
        : calculateCartEarn(checkoutItems, activePromotions, pointsPer100, total),
    [isSaleCheckout, checkoutItems, activePromotions, pointsPer100, total],
  );

  const handleToggle = (checked: boolean) => {
    setLoyaltyPointsToRedeem(checked && canRedeem ? maxRedeemable : null);
  };

  return (
    <div className="sticky top-6 rounded-2xl border border-borderDefault bg-bgWhite p-5">
      <Heading variant="h3" tone="dark" className="mb-4">
        Order Summary
      </Heading>

      <div className="flex flex-col gap-3">
        <div className="mb-3 flex max-h-[400px] flex-col gap-4 overflow-y-auto">
          {displayLines.map((line) => {
            const thumbBase = "h-16 w-16 shrink-0 overflow-hidden rounded-xl bg-bgLight";
            const thumbInner = line.image ? (
              <Image image={line.image} alt={line.alt} size="w-full h-full object-cover" />
            ) : (
              <div className="flex h-full w-full items-center justify-center">
                <Text variant="bodyXs" tone="muted">
                  {line.title ? line.title.charAt(0).toUpperCase() : "—"}
                </Text>
              </div>
            );
            return (
            <div key={line.key} className="flex gap-4 pb-4 border-b border-borderDefault last:border-b-0">
              {line.detailsUrl ? (
                <Link to={line.detailsUrl} aria-label={`View ${line.title}`} className={`${thumbBase} hover:opacity-90 transition-opacity`}>
                  {thumbInner}
                </Link>
              ) : (
                <div className={thumbBase}>{thumbInner}</div>
              )}
              <div className="min-w-0 flex-1">
                {line.detailsUrl ? (
                  <Link to={line.detailsUrl} className="hover:text-brandPrimary transition-colors">
                    <Text variant="body" tone="dark" className="truncate">{line.title}</Text>
                  </Link>
                ) : (
                  <Text variant="body" tone="dark" className="truncate">{line.title}</Text>
                )}
                <div className="mt-1 flex items-center gap-2">
                  <Text variant="bodyXs" tone="body">
                    Qty: {line.quantity}
                  </Text>
                  {"discount" in line && (line as { discount: number }).discount > 0 && (
                    <span className="rounded-md bg-accentGreenTint px-1.5 py-0.5 text-[11px] font-semibold text-accentGreenDark">
                      -{(line as { discount: number }).discount}%
                    </span>
                  )}
                </div>
                {"unitPrice" in line && "discount" in line && (line as { discount: number }).discount > 0 && (
                  <Text variant="bodyXs" tone="muted" className="mt-0.5">
                    <CurrencyAmount amount={(line as { unitPrice: number }).unitPrice} currency={effectiveCurrency} variant="bodyXs" tone="muted" /> /unit
                  </Text>
                )}
                <Text variant="body" tone="dark" className="mt-1">
                  <CurrencyAmount amount={line.amount} currency={effectiveCurrency} />
                </Text>
              </div>
            </div>
            );
          })}
        </div>

        <div className="flex items-center justify-between">
          <Text variant="body" tone="body">Subtotal ({totalItems} {totalItems === 1 ? "item" : "items"})</Text>
          <CurrencyAmount amount={productsSubtotal} currency={effectiveCurrency} variant="body" tone="dark" />
        </div>
        {warrantyLines.length > 0 && (
          <div className="flex flex-col gap-1.5 rounded-xl border border-borderDefault bg-bgLight p-3">
            <div className="flex items-center justify-between">
              <Text variant="bodySmBold" tone="dark">Extended warranties</Text>
              <CurrencyAmount
                amount={warrantiesSubtotal}
                currency={effectiveCurrency}
                variant="bodySmBold"
                tone="dark"
              />
            </div>
            {warrantyLines.map((w) => (
              <div key={w.key} className="flex items-start justify-between gap-3">
                <div className="flex flex-col gap-0.5 min-w-0">
                  <Text variant="bodyXs" tone="dark" className="truncate">
                    {w.warrantyName}
                  </Text>
                  <Text variant="bodyXs" tone="muted" className="truncate">
                    for {w.productName} × {w.quantity}
                  </Text>
                </div>
                <CurrencyAmount
                  amount={w.lineTotal}
                  currency={effectiveCurrency}
                  variant="bodyXs"
                  tone="dark"
                />
              </div>
            ))}
          </div>
        )}
        <div className="flex items-center justify-between">
          <Text variant="body" tone="body">
            {vat != null ? taxLabel(vat, subtotal) : "Tax"}
          </Text>
          {vat != null ? (
            <CurrencyAmount amount={vat} currency={effectiveCurrency} variant="body" tone="dark" />
          ) : (
            <Text variant="body" tone="muted">Calculated at checkout</Text>
          )}
        </div>
        <div className="flex items-center justify-between">
          <Text variant="body" tone="body">Delivery</Text>
          <Text variant="body" tone="dark">Included</Text>
        </div>
        {safeAppliedPoints > 0 && (
          <div className="flex items-center justify-between">
            <Text variant="body" tone="body">
              Discount ({safeAppliedPoints.toLocaleString()} pts)
            </Text>
              <Text variant="body" tone="dark" inline>
              -<GatedPrice>{effectiveCurrency} {loyaltySavings.toFixed(2)}</GatedPrice>
            </Text>
          </div>
        )}
      </div>

      <div className="my-4 border-t border-borderDefault" />

      <div className="flex items-center justify-between">
        <Heading variant="h3" tone="dark">Total</Heading>
        <CurrencyAmount amount={total} currency={effectiveCurrency} variant="h3" tone="dark" />
      </div>

      {earn.total > 0 && (
        <div className="mt-2">
          <LoyaltyEarnSummary earn={earn} />
        </div>
      )}

      <div className="my-4 border-t border-borderDefault" />

      {!isSaleCheckout && (
        <div className="flex items-center justify-between gap-3 rounded-xl border border-borderDefault p-3">
          <div className="flex flex-col gap-0.5">
            <Text variant="bodyBold" tone="dark">
              {canRedeem
                ? `Apply ${maxRedeemable.toLocaleString()} points`
                : balance > 0
                  ? `${balance.toLocaleString()} points available`
                  : "No loyalty points available"}
            </Text>
            <Text variant="bodySm" tone="muted">
              {canRedeem ? (
                <>
                  Save{" "}
                  <GatedPrice>
                    {effectiveCurrency} {((maxRedeemable / 100) * redemptionRate).toFixed(2)}
                  </GatedPrice>{" "}
                  on this order
                </>
              ) : balance > 0 ? (
                "Add more items to redeem points"
              ) : (
                "Earn points on every purchase"
              )}
            </Text>
          </div>
          <Switch
            checked={safeAppliedPoints > 0}
            onChange={handleToggle}
            disabled={!canRedeem}
            aria-label="Apply loyalty points"
          />
        </div>
      )}

      <Button
        variant="primary"
        fullWidth
        className="mt-4"
        onClick={onProceed}
        disabled={proceedDisabled}
      >
        {buttonLabel}
      </Button>

      <div className="mt-4 flex items-center justify-center gap-1.5">
        <IconShieldCheck size={18} className="text-accentCyan700" />
        <img src={logo} alt="Procurtal" className="h-5" />
        <Text variant="bodySm" tone="muted" inline>ensures</Text>
        <Text variant="bodySmBold" tone="dark" inline>a safe and reliable checkout</Text>
      </div>
    </div>
  );
}
